<script setup>
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from '../LanguageSwitcher.vue'

defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  isLoggedIn: {
    type: Boolean,
    required: true
  },
  isAdmin: {
    type: Boolean,
    default: false
  },
  username: {
    type: String,
    default: ''
  },
  accentColors: {
    type: Object,
    required: true
  },
  accentColor: {
    type: String,
    required: true
  },
  isDark: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close', 'toggleTheme', 'setAccentColor'])

const { t } = useI18n()
const route = useRoute()

const handleAccentColorChange = (colorKey) => {
  emit('setAccentColor', colorKey)
}

const closeMenu = () => {
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        @click="closeMenu"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] lg:hidden"
        aria-hidden="true"
      ></div>
    </Transition>

    <!-- Sidebar Drawer -->
    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-250 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div
        v-if="isOpen"
        class="fixed top-0 right-0 bottom-0 w-[360px] max-w-[90vw] bg-m3-surface z-[110] lg:hidden shadow-2xl shadow-black/25 flex flex-col overflow-y-auto rounded-l-3xl border-l border-m3-outline-variant"
        role="dialog"
        aria-modal="true"
        :aria-label="t('nav.main_menu_aria')"
      >
        <!-- Header -->
        <div class="p-5 flex justify-between items-center bg-brand-background/50">
          <span class="text-lg font-bold text-brand-text tracking-tight">{{ t('common.menu') }}</span>
          <button
            @click="closeMenu"
            class="p-2 -mr-2 text-brand-muted hover:text-brand-text min-h-[48px] min-w-[48px] flex items-center justify-center rounded-full hover:bg-brand-muted/10 transition-colors"
            :aria-label="t('nav.close_menu_aria')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Navigation Section -->
        <nav class="px-3 pt-2 pb-4">
          <p class="px-4 pb-2 text-xs font-bold text-brand-muted uppercase tracking-wider">{{ t('common.browse') }}</p>
          <div class="space-y-1">
            <RouterLink
              @click="closeMenu"
              to="/"
              class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              {{ t('nav.all_media') }}
            </RouterLink>
            <RouterLink
              @click="closeMenu"
              to="/albums"
              class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
              {{ t('nav.albums') }}
            </RouterLink>
            <RouterLink
              @click="closeMenu"
              to="/releases"
              class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {{ t('nav.releases') }}
            </RouterLink>
            <RouterLink
              @click="closeMenu"
              to="/charts"
              class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z"/>
                <path d="M19 19H5v-3h14v3z" opacity="0.5"/>
              </svg>
              {{ t('nav.charts') }}
            </RouterLink>
            <div
              class="flex items-center gap-3 px-4 py-3 text-brand-muted/50 cursor-not-allowed rounded-[16px] min-h-[52px]"
              aria-disabled="true"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>{{ t('nav.concerts') }}</span>
              <span class="ml-auto text-[10px] font-bold uppercase tracking-widest text-brand-muted/40">{{ t('nav.soon') }}</span>
            </div>
            <RouterLink
              @click="closeMenu"
              to="/festivals"
              class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              {{ t('nav.festivals') }}
            </RouterLink>
          </div>
        </nav>

        <!-- Divider -->
        <div class="mx-6 border-t border-brand-muted/10"></div>

        <!-- Account Section -->
        <nav class="px-3 pt-4 pb-4">
          <p class="px-4 pb-2 text-xs font-bold text-brand-muted uppercase tracking-wider">{{ t('footer.account') }}</p>
          <div class="space-y-1">
            <template v-if="isLoggedIn">
              <RouterLink
                @click="closeMenu"
                to="/profile"
                class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span class="truncate">{{ username }}</span>
              </RouterLink>
              <RouterLink
                @click="closeMenu"
                to="/my-festivals"
                class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span>{{ t('attendance.my_festivals') }}</span>
              </RouterLink>
              <RouterLink
                v-if="isAdmin"
                @click="closeMenu"
                to="/admin"
                class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{{ t('nav.admin') }}</span>
              </RouterLink>
            </template>
            <template v-else>
              <RouterLink
                @click="closeMenu"
                :to="route.name === 'login' || route.name === 'register'
                  ? { name: 'login' }
                  : { name: 'login', query: { redirect: route.fullPath } }"
                class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all active:scale-[0.98] min-h-[52px]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>{{ t('nav.login') }}</span>
              </RouterLink>
            </template>
          </div>
        </nav>

        <!-- Divider -->
        <div class="mx-6 border-t border-brand-muted/10"></div>

        <!-- Settings Section -->
        <div class="mt-auto p-4 bg-brand-background/20">
          <!-- Accent Color Selector -->
          <div class="px-2 py-2 mb-2">
            <div class="flex items-center gap-2 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              <span class="text-xs font-bold text-brand-muted uppercase tracking-widest">{{ t('theme.accent') }}</span>
            </div>
            <div class="flex items-center gap-1.5 justify-center">
              <button
                v-for="(color, key) in accentColors"
                :key="key"
                @click="handleAccentColorChange(key)"
                class="w-8 h-8 rounded-full border-2 transition-all duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
                :class="accentColor === key ? 'border-brand-text scale-110' : 'border-transparent'"
                :style="{ backgroundColor: `rgb(${isDark ? color.dark.primary : color.light.primary})` }"
                :title="color.name"
                :aria-label="`Select ${color.name} accent color`"
                :aria-pressed="accentColor === key"
              ></button>
            </div>
          </div>

          <!-- Theme Toggle -->
          <div class="flex items-center justify-between px-2 py-2">
            <button
              @click="$emit('toggleTheme')"
              class="flex items-center gap-3 px-4 py-3 text-brand-text hover:text-brand-accent hover:bg-brand-muted/5 rounded-[16px] font-medium transition-all min-h-[48px] flex-1"
            >
              <svg v-if="isDark" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              <span>{{ isDark ? t('theme.light_mode') : t('theme.dark_mode') }}</span>
            </button>
            <div class="flex items-center gap-2 pl-2 border-l border-brand-muted/20 ml-2">
              <span class="text-xs font-bold text-brand-muted uppercase tracking-widest">{{ t('nav.language') }}</span>
              <LanguageSwitcher :show-full-name="false" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>